import { Network, validate } from 'bitcoin-address-validation';
import type {
  CryptoAssetBalanceParams,
  CryptoAssetBalanceResult,
  CryptoAssetEstimateGasParams,
  CryptoAssetGasEstimate,
  CryptoAssetMaxSendAmountParams,
  CryptoAssetMaxSendAmountResult,
  CryptoAssetTransactionHistoryResult,
  CryptoAssetTransactionHistoryParams,
  CryptoAssetSendParams,
  CryptoAssetTransactionResult,
  ICryptoAssetProvider,
} from './ICryptoAssetProvider';
import { SLIP44, type CryptoAsset } from '@/types/WalletTypes';
import type { WalletServiceClient } from '@/resources/wallet-service.client';
import { getSameFeeAssetMaxSendAmount, getWalletServiceTransactionHistory, requireMnemonic } from './shared';

const getBitcoinNetworkName = (cryptoAsset: CryptoAsset) =>
  cryptoAsset.asset.blockchainConfiguration.slip44CoinType === SLIP44.BITCOIN ? Network.mainnet : Network.testnet;

const getBitcoinNetworkType = (cryptoAsset: CryptoAsset) =>
  cryptoAsset.asset.blockchainConfiguration.slip44CoinType === SLIP44.BITCOIN ? 'main' : 'test3';

export class BitcoinCryptoAssetProvider implements ICryptoAssetProvider {
  readonly name = 'bitcoin';

  constructor(private readonly walletServiceClient: WalletServiceClient) {}

  supports(cryptoAsset: CryptoAsset): boolean {
    return cryptoAsset.traits.isBitcoin;
  }

  validateAddress(address: string, cryptoAsset: CryptoAsset): boolean {
    return validate(address, getBitcoinNetworkName(cryptoAsset));
  }

  getTransactionHistory(params: CryptoAssetTransactionHistoryParams): Promise<CryptoAssetTransactionHistoryResult> {
    return getWalletServiceTransactionHistory({ ...params, walletServiceClient: this.walletServiceClient });
  }

  async getBalance({ address, cryptoAsset }: CryptoAssetBalanceParams): Promise<CryptoAssetBalanceResult> {
    if (!address) {
      return { balance: null, canSendFunds: false, hasError: true };
    }
    try {
      const response = await this.walletServiceClient.getBitcoinBalance({
        address,
        network: getBitcoinNetworkType(cryptoAsset),
      });
      if (!response?.balance) {
        return { balance: null, canSendFunds: false, hasError: true };
      }
      const currentBalance = response.balance;
      const finalBalance = (response?.rawData?.final_balance / 100000000).toFixed(8);
      const balance = Math.min(currentBalance, Number(finalBalance));

      return { balance, canSendFunds: true, hasError: false };
    } catch {
      return { balance: null, canSendFunds: false, hasError: true };
    }
  }

  async estimateGas(params: CryptoAssetEstimateGasParams): Promise<CryptoAssetGasEstimate> {
    const { btc, usd } = await this.walletServiceClient.estimateGas({
      amount: params.amount,
      core: params.core,
      symbol: params.cryptoAsset.asset.canonicalName,
      to: params.to,
    });
    return {
      coin: Number(btc),
      usd: Number(usd),
    };
  }

  getMaxSendAmount(params: CryptoAssetMaxSendAmountParams): CryptoAssetMaxSendAmountResult {
    return getSameFeeAssetMaxSendAmount(params);
  }

  async send({
    amount,
    cryptoAsset,
    from,
    isMaxSend,
    mnemonic,
    privateKey,
    to,
  }: CryptoAssetSendParams): Promise<CryptoAssetTransactionResult> {
    const networkType = getBitcoinNetworkType(cryptoAsset);
    if (privateKey) {
      return sendLegacyBTCTransaction({ to, value: amount, privateKey, networkType, from, isMaxSend });
    }
    return sendBTCTransaction({
      to,
      value: amount,
      mnemonic: requireMnemonic(mnemonic, 'Bitcoin transactions'),
      networkType,
      from,
      isMaxSend,
    });
  }
}

async function sendBTCTransaction({ to, value, mnemonic, networkType, from, isMaxSend }) {
  try {
    const response = await fetch('/api/coins/btcSendTxn', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        toAddress: to,
        amount: value,
        mnemonic,
        network: networkType,
        sourceAddress: from,
        isMaxSend,
      }),
    });
    const data = await response.json();
    if (!response.ok) {
      throw Object.assign(new Error(data.error || 'Failed to send BTC transaction'), { ErrorCode: 60005 });
    }

    return {
      success: true,
      hash: data.hash,
      network: data.network,
    };
  } catch (err) {
    if (err.ErrorCode) {
      throw err;
    }
    throw Object.assign(new Error(`Failed to send BTC transaction: ${err.message}`), { ErrorCode: 60005 });
  }
}

async function sendLegacyBTCTransaction({ to, value, privateKey, networkType, from, isMaxSend }) {
  try {
    const response = await fetch('/api/coins/btcSendTxn', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        toAddress: to,
        amount: value,
        privateKey,
        network: networkType,
        sourceAddress: from,
        isMaxSend,
        isLegacy: true,
      }),
    });
    const data = await response.json();
    if (!response.ok) {
      throw Object.assign(new Error(data.error || 'Failed to send legacy BTC transaction'), { ErrorCode: 60005 });
    }

    return {
      success: true,
      hash: data.hash,
      network: data.network,
    };
  } catch (err) {
    if (err.ErrorCode) {
      throw err;
    }
    throw Object.assign(new Error(`Failed to send legacy BTC transaction: ${err.message}`), { ErrorCode: 60005 });
  }
}
